import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../firebase/AuthProvider';
import { Message } from '../../../services/chatService/ChatService';
import { formatFileSize } from '../../../utilities/fileUtils';
import { Loader2, FileText, FileSpreadsheet, File as FileIcon, Download, Image as ImageIcon, X, Maximize2 } from 'lucide-react';
import { getAttachmentDisplayName, isImageAttachment } from './attachmentPresentation';

interface AttachmentDisplayProps {
    message: Message;
    attachmentUrl: string;
    attachmentType: string;
}

const AttachmentDisplay: React.FC<AttachmentDisplayProps> = ({ message }) => {
    const { currentUser } = useAuth();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const isImage = isImageAttachment(message);

    const fetchAttachmentBlob = useCallback(async (): Promise<Blob> => {
        if (!currentUser || !message.id) throw new Error('Authentication or message ID is missing.');

        const token = await currentUser.getIdToken();
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/message/${message.id}/attachment`;
        const response = await fetch(apiUrl, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
        return response.blob();
    }, [currentUser, message.id]);

    useEffect(() => {
        if (!isImage) return;

        let objectUrl: string | undefined;
        const fetchAndDisplayImage = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const blob = await fetchAttachmentBlob();
                objectUrl = URL.createObjectURL(blob);
                setImageSrc(objectUrl);
            } catch (err) {
                console.error('Failed to load attachment image:', err);
                setError('Failed to load image.');
            } finally {
                setIsLoading(false);
            }
        };

        void fetchAndDisplayImage();
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isImage, fetchAttachmentBlob]);

    useEffect(() => {
        if (!isPreviewOpen) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsPreviewOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isPreviewOpen]);

    const handleDownload = async () => {
        setError(null);
        try {
            const blob = await fetchAttachmentBlob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = message.attachmentOriginalName || `attachment-${message.id}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        } catch (downloadError) {
            console.error('Failed to download attachment:', downloadError);
            setError('Download failed.');
        }
    };

    const getFileIcon = (fileType: string) => {
        if (!fileType) return <FileIcon className="text-slate-500" size={24} />;
        const ext = fileType.toLowerCase();
        if (['pdf'].includes(ext)) return <FileText className="text-red-500" size={24} />;
        if (['xls', 'xlsx', 'excel'].includes(ext)) return <FileSpreadsheet className="text-green-600" size={24} />;
        if (['doc', 'docx'].includes(ext)) return <FileText className="text-blue-500" size={24} />;
        return <FileIcon className="text-slate-500" size={24} />;
    };

    if (isImage) {
        if (isLoading) {
            return (
                <div className="chat-attachment-card mt-2 flex h-40 w-60 items-center justify-center rounded-xl bg-slate-50">
                    <Loader2 className="animate-spin text-slate-400" size={28} />
                </div>
            );
        }

        if (error || !imageSrc) {
            return (
                <div className="mt-2">
                    <button onClick={handleDownload} className="chat-attachment-card inline-flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 text-sm font-medium text-[#7E57C2] transition-colors hover:bg-slate-100">
                        <ImageIcon size={18} />
                        <span>Download image</span>
                    </button>
                </div>
            );
        }

        return (
            <>
                <button
                    type="button"
                    onClick={() => setIsPreviewOpen(true)}
                    className="group relative mt-2 block cursor-zoom-in appearance-none overflow-hidden rounded-xl border border-slate-200 bg-transparent p-0 shadow-sm"
                    aria-label="Open image preview"
                >
                    <img
                        src={imageSrc}
                        alt={message.attachmentType || 'attachment'}
                        className="max-h-60 max-w-full object-cover transition-transform duration-200 group-hover:scale-[1.01]"
                    />
                    <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg bg-slate-950/60 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                        <Maximize2 size={14} />
                    </span>
                </button>

                {isPreviewOpen && typeof document !== 'undefined' && createPortal(
                    <div
                        className="fixed inset-0 z-[12500] flex items-center justify-center bg-slate-950/90 p-4 sm:p-8"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Image preview"
                        onMouseDown={event => {
                            if (event.target === event.currentTarget) setIsPreviewOpen(false);
                        }}
                    >
                        <div className="absolute right-4 top-4 z-10 flex items-center gap-2 sm:right-6 sm:top-6">
                            <button
                                type="button"
                                onClick={handleDownload}
                                className="flex h-10 items-center gap-2 rounded-xl border border-white/15 bg-black/30 px-3 text-sm font-semibold text-white backdrop-blur-md transition-colors hover:bg-white/15"
                            >
                                <Download size={16} />
                                <span className="hidden sm:inline">Download</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsPreviewOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-black/30 text-white backdrop-blur-md transition-colors hover:bg-white/15"
                                aria-label="Close image preview"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <img
                            src={imageSrc}
                            alt={message.attachmentType || 'attachment preview'}
                            className="max-h-[calc(100vh-64px)] max-w-[calc(100vw-32px)] select-none object-contain shadow-2xl sm:max-h-[calc(100vh-96px)] sm:max-w-[calc(100vw-96px)]"
                            onMouseDown={event => event.stopPropagation()}
                        />
                    </div>,
                    document.body,
                )}
            </>
        );
    }

    const displayName = getAttachmentDisplayName(message);
    const fileType = displayName.includes('.')
        ? displayName.split('.').pop() || message.attachmentType || 'file'
        : message.attachmentType || 'file';

    return (
        <div className="mb-1 mt-1 block max-w-full">
            <button
                type="button"
                onClick={() => void handleDownload()}
                className="chat-attachment-card group flex w-[min(22rem,calc(100vw-7rem))] max-w-full items-center justify-start gap-3 rounded-xl bg-white p-2.5 shadow-sm transition-all hover:bg-purple-50"
                title={`Download ${displayName}`}
            >
                <div className="chat-attachment-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-colors group-hover:bg-white">
                    {getFileIcon(fileType)}
                </div>
                <div className="flex min-w-0 flex-1 flex-col text-left">
                    <span className="chat-attachment-name block max-w-full truncate text-sm font-medium text-slate-800">{displayName}</span>
                    {typeof message.attachmentSize === 'number' && (
                        <span className="chat-attachment-size truncate text-[10px] text-slate-400">{formatFileSize(message.attachmentSize)}</span>
                    )}
                    <span className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        <Download size={10} /> Download
                    </span>
                </div>
            </button>
        </div>
    );
};

export default AttachmentDisplay;
